import { parseSpreadsheetValidationError } from '@/shared/utils/parseSpreadsheetValidationError';
import { throwApiError } from '@/shared/utils/apiResponse';
import { apiGet, apiPost, apiPut } from '@/shared/utils/requestBuilder';
import type { CorrecaoNomesLoteResult } from '@/shared/types/correcaoNomes';

import type {
  ClosePhoneConflictResult,
  ValidatePhoneConflictResult,
} from '@/features/negociacaoIndividualWizard/types';
import type {
  ArchiveNegociacaoCasoResult,
  CancelNegociacaoStartResult,
  CloseNegociacaoCasoResult,
  EncerrarNegociacaoCasoResult,
  ListNegociacaoCasosParams,
  ListNegociacaoCasosResponse,
  NegociacaoPeriodoParams,
  ReabrirNegociacaoCasoResult,
  UnarchiveNegociacaoCasoResult,
  UpdateNegociacaoCasoStatusResult,
} from '../types';
import type {
  BulkEncerramentoInput,
  BulkEncerramentoResult,
  EncerramentoNegociacaoInput,
  EncerrarAtendimentoHumanoResult,
  ReaberturaNegociacaoInput,
} from '../types/encerramentoTypes';
import type {
  CpfCnpjLookupResult,
  MapeamentoColunas,
  NegociacaoItemEditData,
  NegociacaoItemEditInput,
} from '../types/batch.types';
import type { NegociacaoLoteStatusEvent, NegociacaoLoteTotais } from '../types/loteStatus.types';
import type { NegociacaoLoteItemDTO } from '../types/lote.types';
import type { AjusteNegociacaoGrupo, ReenviarItemLoteResult } from '../types/verificacao.types';
import type {
  DashboardCasesPage,
  EngagementCategory,
  FunnelStage,
  GeneralDashboard,
  QualitativeDashboard,
} from '../types';
import { ENGAGEMENT_CATEGORY_SLUGS, FUNNEL_STAGE_SLUGS } from '../types';
import type {
  LoteDetailParams,
  LoteDetailResponse,
  ListLotesParams,
  ListLotesResponse,
} from '../types/lotes.types';

const unwrapSuccessEnvelope = <T>(json: unknown): T => {
  const typed = json as
    | { success: true; data: T }
    | { success: false; error?: { message?: string } }
    | T;

  if (typeof typed === 'object' && typed && 'success' in typed) {
    if ((typed as { success: boolean }).success === true) {
      return (typed as { data: T }).data;
    }

    const msg = (typed as { error?: { message?: string } })?.error?.message;
    throw new Error(msg || 'Erro na requisição');
  }

  return typed as T;
};

const parseSuccessResponse = async <T>(response: Response, defaultMsg: string): Promise<T> => {
  if (!response.ok) {
    await throwApiError(response, defaultMsg);
  }

  const json = (await response.json()) as unknown;
  return unwrapSuccessEnvelope<T>(json);
};

export type { NegociacaoLoteItemDTO } from '../types/lote.types';

const buildListCasosUrl = (params: ListNegociacaoCasosParams): string => {
  const queryParams = new URLSearchParams();
  queryParams.append('empresaId', params.empresaId);

  if (params.escritorioId) queryParams.append('escritorioId', params.escritorioId);
  if (params.extrajudicialItemId)
    queryParams.append('extrajudicialItemId', params.extrajudicialItemId);
  if (params.search) queryParams.append('search', params.search);
  if (params.type) queryParams.append('type', params.type);

  if (params.statusIn && params.statusIn.length > 0) {
    params.statusIn.forEach((status) => queryParams.append('statusIn[]', status));
  } else if (params.status) {
    queryParams.append('status', params.status);
  }

  if (params.tabCountsStatusIn && params.tabCountsStatusIn.length > 0) {
    params.tabCountsStatusIn.forEach((status) => queryParams.append('tabCountsStatusIn[]', status));
  } else if (params.tabCountsStatus) {
    queryParams.append('tabCountsStatus', params.tabCountsStatus);
  }

  if (params.cadenceBucketIn && params.cadenceBucketIn.length > 0) {
    params.cadenceBucketIn.forEach((bucket) => queryParams.append('cadenceBucketIn[]', bucket));
  }

  if (params.humanIntervention !== undefined) {
    queryParams.append('humanIntervention', String(params.humanIntervention));
  }

  if (params.tabCountsHumanIntervention !== undefined) {
    queryParams.append('tabCountsHumanIntervention', String(params.tabCountsHumanIntervention));
  }

  if (params.archived !== undefined) {
    queryParams.append('archived', String(params.archived));
  }

  if (params.tipoEncerramentoIn && params.tipoEncerramentoIn.length > 0) {
    params.tipoEncerramentoIn.forEach((tipo) => queryParams.append('tipoEncerramentoIn[]', tipo));
  } else if (params.tipoEncerramento) {
    queryParams.append('tipoEncerramento', params.tipoEncerramento);
  }

  if (params.scenarioTypeIn && params.scenarioTypeIn.length > 0) {
    params.scenarioTypeIn.forEach((scenario) => queryParams.append('scenarioTypeIn[]', scenario));
  }

  if (params.situacaoIdIn && params.situacaoIdIn.length > 0) {
    params.situacaoIdIn.forEach((id) => queryParams.append('situacaoIdIn[]', id));
  }

  if (params.pendency) {
    queryParams.append('pendency', params.pendency);
  }

  if (params.periodoInicio) {
    queryParams.append('periodoCampo', params.periodoCampo ?? 'criacao');
    queryParams.append('periodoInicio', params.periodoInicio);
    if (params.periodoFim) queryParams.append('periodoFim', params.periodoFim);
  }

  if (params.page) queryParams.append('page', String(params.page));
  if (params.limit) queryParams.append('limit', String(params.limit));
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

  const query = queryParams.toString();
  return query ? `/negociacao/casos?${query}` : '/negociacao/casos';
};

export interface NegociacaoSituacaoListItemDTO {
  id: string;
  empresaId: string;
  escritorioId: string | null;
  key: string;
  label: string;
  description: string | null;
  active: boolean;
  isShared: boolean;
}

export interface ListNegociacaoSituacoesResponse {
  items: NegociacaoSituacaoListItemDTO[];
}

export const negociacoesService = {
  listCasos: async (params: ListNegociacaoCasosParams): Promise<ListNegociacaoCasosResponse> => {
    const url = buildListCasosUrl(params);
    const response = await apiGet(url);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao listar casos de negociação');
    }

    const body = (await response.json()) as unknown;
    return body as ListNegociacaoCasosResponse;
  },

  listSituacoes: async (): Promise<ListNegociacaoSituacoesResponse> => {
    const response = await apiGet('/negociacao/situacoes?active=true');
    if (!response.ok) {
      await throwApiError(response, 'Erro ao listar situações');
    }

    const body = (await response.json()) as unknown;
    return body as ListNegociacaoSituacoesResponse;
  },

  pauseCaso: async (caseId: string): Promise<UpdateNegociacaoCasoStatusResult> => {
    const response = await apiPost(`/negociacao/casos/${caseId}/pause`, {});
    return parseSuccessResponse<UpdateNegociacaoCasoStatusResult>(
      response,
      'Erro ao pausar caso de negociação',
    );
  },

  resumeCaso: async (caseId: string): Promise<UpdateNegociacaoCasoStatusResult> => {
    const response = await apiPost(`/negociacao/casos/${caseId}/resume`, {});
    return parseSuccessResponse<UpdateNegociacaoCasoStatusResult>(
      response,
      'Erro ao reiniciar caso de negociação',
    );
  },

  archiveCaso: async (caseId: string): Promise<ArchiveNegociacaoCasoResult> => {
    const response = await apiPost(`/negociacao/casos/${caseId}/archive`, {});
    return parseSuccessResponse<ArchiveNegociacaoCasoResult>(
      response,
      'Erro ao arquivar caso de negociação',
    );
  },

  unarchiveCaso: async (caseId: string): Promise<UnarchiveNegociacaoCasoResult> => {
    const response = await apiPost(`/negociacao/casos/${caseId}/unarchive`, {});
    return parseSuccessResponse<UnarchiveNegociacaoCasoResult>(
      response,
      'Erro ao desarquivar caso de negociação',
    );
  },

  closeCaso: async (
    caseId: string,
    resultStatus: CloseNegociacaoCasoResult['resultStatus'],
  ): Promise<CloseNegociacaoCasoResult> => {
    const response = await apiPost(`/negociacao/casos/${caseId}/close`, { resultStatus });
    return parseSuccessResponse<CloseNegociacaoCasoResult>(
      response,
      'Erro ao encerrar caso de negociação',
    );
  },

  downloadModeloCsv: async (flowId: string): Promise<Blob> => {
    const response = await apiGet(`/negociacao/lotes/template?flowId=${flowId}`);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao baixar modelo de planilha');
    }
    return response.blob();
  },

  uploadCsv: async (params: {
    flowId: string;
    title: string;
    file: File;
    mapeamento?: MapeamentoColunas;
    ignoreRows?: number[];
  }): Promise<{ loteId: string }> => {
    const formData = new FormData();
    formData.append('flowId', params.flowId);
    formData.append('title', params.title);
    if (params.mapeamento) {
      formData.append('mapeamento', JSON.stringify(params.mapeamento));
    }
    if (params.ignoreRows && params.ignoreRows.length > 0) {
      formData.append('ignoreRows', JSON.stringify(params.ignoreRows));
    }
    formData.append('file', params.file);

    const response = await apiPost('/negociacao/lotes/upload-csv', formData);
    if (!response.ok) {
      const validationError = await parseSpreadsheetValidationError(response);
      if (validationError) throw validationError;
      await throwApiError(response, 'Erro ao realizar upload da planilha');
    }

    const body = (await response.json()) as { success: boolean; data: { loteId: string } };
    return body.data;
  },

  // `/negociacao/lotes/:id` devolve o detalhe `{ lote, casos }`, com totais de casos; o fim da
  // validação depende dos totais de itens, que só esta rota (mesmo formato do evento SSE) expõe.
  getLoteStatus: async (loteId: string): Promise<NegociacaoLoteStatusEvent> => {
    const response = await apiGet(`/negociacao/lotes/${loteId}/status`);
    return parseSuccessResponse<NegociacaoLoteStatusEvent>(
      response,
      'Erro ao consultar status do lote',
    );
  },

  getLoteDetail: async (loteId: string, params?: LoteDetailParams): Promise<LoteDetailResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', String(params.page));
    if (params?.limit !== undefined) queryParams.append('limit', String(params.limit));
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);

    const query = queryParams.toString();
    const url = query ? `/negociacao/lotes/${loteId}?${query}` : `/negociacao/lotes/${loteId}`;
    const response = await apiGet(url);
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as Record<string, unknown> | null;
      const message =
        (typeof body?.message === 'string' ? body.message : null) ??
        (typeof body?.error === 'string' ? body.error : null) ??
        'Erro ao carregar detalhes do lote';
      const err = Object.assign(new Error(message), { statusCode: response.status });
      throw err;
    }
    return parseSuccessResponse<LoteDetailResponse>(response, 'Erro ao carregar detalhes do lote');
  },

  listLotes: async (params: ListLotesParams): Promise<ListLotesResponse> => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    if (params.page !== undefined) queryParams.append('page', String(params.page));
    if (params.limit !== undefined) queryParams.append('limit', String(params.limit));

    const query = queryParams.toString();
    const url = query ? `/negociacao/lotes?${query}` : '/negociacao/lotes';
    const response = await apiGet(url);
    return parseSuccessResponse<ListLotesResponse>(response, 'Erro ao listar lotes de negociação');
  },

  listLoteItems: async (
    loteId: string,
    params: {
      page?: number;
      pageSize?: number;
      status?: string;
    },
  ): Promise<{
    items: NegociacaoLoteItemDTO[];
    total: number;
    totais: NegociacaoLoteTotais;
  }> => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', String(params.page));
    if (params.pageSize) queryParams.append('pageSize', String(params.pageSize));
    if (params.status) queryParams.append('status', params.status);

    const response = await apiGet(`/negociacao/lotes/${loteId}/items?${queryParams.toString()}`);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao listar itens do lote');
    }
    const body = (await response.json()) as {
      success: boolean;
      data: { items: NegociacaoLoteItemDTO[]; total: number; totais: NegociacaoLoteTotais };
    };
    return body.data;
  },

  removeLoteItem: async (loteId: string, itemId: string): Promise<void> => {
    const response = await apiPost(`/negociacao/lotes/${loteId}/items/${itemId}/remove`, {});
    if (!response.ok) {
      await throwApiError(response, 'Erro ao remover item do lote');
    }
  },

  discardAcordosExistentes: async (loteId: string): Promise<{ discarded: number }> => {
    const response = await apiPost(`/negociacao/lotes/${loteId}/acordos/discard`, {});
    return parseSuccessResponse<{ discarded: number }>(
      response,
      'Erro ao descartar acordos existentes',
    );
  },

  corrigirNomesEmLote: async (loteId: string): Promise<CorrecaoNomesLoteResult> => {
    const response = await apiPost(`/negociacao/lotes/${loteId}/corrigir-nomes`, {});
    if (!response.ok) {
      await throwApiError(response, 'Erro ao corrigir nomes em lote');
    }
    const body = (await response.json()) as { success: boolean; data: CorrecaoNomesLoteResult };
    return body.data;
  },

  confirmLote: async (
    loteId: string,
    params: { startMode: 'NOW' | 'SCHEDULED'; scheduledFor?: string },
  ): Promise<{ loteId: string }> => {
    const response = await apiPost(`/negociacao/lotes/${loteId}/confirm`, params);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao confirmar lote');
    }
    const body = (await response.json()) as { success: boolean; data: { loteId: string } };
    return body.data;
  },

  listarAjustesNegociacao: async (loteId?: string): Promise<AjusteNegociacaoGrupo[]> => {
    const query = loteId ? `?loteId=${encodeURIComponent(loteId)}` : '';
    const response = await apiGet(`/negociacao/ajustes${query}`);
    const data = await parseSuccessResponse<{ grupos: AjusteNegociacaoGrupo[] }>(
      response,
      'Erro ao carregar as pendências de ajuste',
    );
    return data.grupos ?? [];
  },

  reenviarItemLote: async (loteId: string, itemId: string): Promise<ReenviarItemLoteResult> => {
    const response = await apiPost(`/negociacao/lotes/${loteId}/itens/${itemId}/reenviar`, {});
    return parseSuccessResponse<ReenviarItemLoteResult>(response, 'Erro ao reenviar contato');
  },

  ignorarItemLote: async (loteId: string, itemId: string): Promise<void> => {
    const response = await apiPost(`/negociacao/lotes/${loteId}/itens/${itemId}/ignorar`, {});
    await parseSuccessResponse<{ ignorado: boolean }>(response, 'Erro ao ignorar pendência');
  },

  reverterItemLote: async (loteId: string, itemId: string): Promise<void> => {
    const response = await apiPost(`/negociacao/lotes/${loteId}/itens/${itemId}/reverter`, {});
    await parseSuccessResponse<{ revertido: boolean }>(response, 'Erro ao reverter pendência');
  },

  obterItemParaEdicao: async (itemId: string): Promise<NegociacaoItemEditData> => {
    const response = await apiGet(`/negociacao/lotes/itens/${itemId}/edit`);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao obter item para edição');
    }
    const body = (await response.json()) as { success: boolean; data: NegociacaoItemEditData };
    return body.data;
  },

  atualizarItem: async (
    itemId: string,
    input: NegociacaoItemEditInput,
  ): Promise<{ success: boolean }> => {
    const response = await apiPut(`/negociacao/lotes/itens/${itemId}`, input);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao atualizar item');
    }
    const body = (await response.json()) as { success: boolean; data: { success: boolean } };
    return body.data;
  },

  validarCpfCnpj: async (documento: string): Promise<CpfCnpjLookupResult> => {
    const response = await apiPost('/integracoes/cpf-cnpj/validar', { documento });
    return parseSuccessResponse<CpfCnpjLookupResult>(response, 'Erro ao validar CPF/CNPJ');
  },

  validatePhoneConflict: async (phone: string): Promise<ValidatePhoneConflictResult> => {
    const response = await apiPost('/negociacao/casos/phone-conflict/validate', { phone });
    return parseSuccessResponse<ValidatePhoneConflictResult>(
      response,
      'Erro ao validar conflito por WhatsApp',
    );
  },

  /** A negociação aberta pelo id — a "em andamento" achada pelo CPF não tem telefone para buscar. */
  getOpenCaseConflict: async (caseId: string): Promise<ValidatePhoneConflictResult> => {
    const response = await apiGet(`/negociacao/casos/phone-conflict/${caseId}`);
    return parseSuccessResponse<ValidatePhoneConflictResult>(
      response,
      'Erro ao consultar negociação em andamento',
    );
  },

  closePhoneConflict: async (caseId: string): Promise<ClosePhoneConflictResult> => {
    const response = await apiPost(`/negociacao/casos/phone-conflict/${caseId}/close`, {});
    return parseSuccessResponse<ClosePhoneConflictResult>(
      response,
      'Erro ao encerrar negociação em conflito',
    );
  },

  encerrarCaso: async (
    input: EncerramentoNegociacaoInput,
  ): Promise<EncerrarNegociacaoCasoResult> => {
    const response = await apiPost(`/negociacao/casos/${input.caseId}/encerrar`, {
      tipo: input.tipo,
      observacoes: input.observacoes,
      detalhesAcordo: input.detalhesAcordo,
      encerrarAtendimentoHumano: input.encerrarAtendimentoHumano,
    });
    return parseSuccessResponse<EncerrarNegociacaoCasoResult>(
      response,
      'Erro ao encerrar negociação',
    );
  },

  encerrarAtendimentoHumano: async (caseId: string): Promise<EncerrarAtendimentoHumanoResult> => {
    const response = await apiPost(`/negociacao/casos/${caseId}/encerrar-atendimento`, {});
    return parseSuccessResponse<EncerrarAtendimentoHumanoResult>(
      response,
      'Erro ao encerrar atendimento humano',
    );
  },

  reabrirCaso: async (input: ReaberturaNegociacaoInput): Promise<ReabrirNegociacaoCasoResult> => {
    const response = await apiPost(`/negociacao/casos/${input.caseId}/reabrir`, {
      justificativa: input.justificativa,
      manterDadosAnteriores: input.manterDadosAnteriores,
    });
    return parseSuccessResponse<ReabrirNegociacaoCasoResult>(
      response,
      'Erro ao reabrir negociação',
    );
  },

  cancelStart: async (caseId: string): Promise<CancelNegociacaoStartResult> => {
    const response = await apiPost(`/negociacao/casos/${caseId}/cancel-start`, {});
    return parseSuccessResponse<CancelNegociacaoStartResult>(
      response,
      'Erro ao cancelar início agendado',
    );
  },

  bulkEncerrarCasos: async (input: BulkEncerramentoInput): Promise<BulkEncerramentoResult> => {
    const response = await apiPost('/negociacao/casos/bulk-encerrar', {
      caseIds: input.caseIds,
      tipo: input.tipo,
      observacoes: input.observacoes,
      detalhesAcordo: input.detalhesAcordo,
    });
    return parseSuccessResponse<BulkEncerramentoResult>(
      response,
      'Erro ao encerrar negociações em lote',
    );
  },

  getGeneralDashboard: async (): Promise<GeneralDashboard> => {
    const response = await apiGet('/extrajudicial/dashboard/negotiations/general');

    if (!response.ok) {
      await throwApiError(response, 'Erro ao obter dashboard geral');
    }

    const body = await response.json();
    return body?.data ?? body;
  },

  getQualitativeDashboard: async (force = false): Promise<QualitativeDashboard> => {
    const params = force ? { force: 'true' } : undefined;
    const response = await apiGet('/extrajudicial/dashboard/negotiations/qualitative', params);

    if (!response.ok) {
      await throwApiError(response, 'Erro ao obter dashboard qualitativo');
    }

    const body = await response.json();
    return body?.data ?? body;
  },

  getFunnelCases: async (
    stage: FunnelStage,
    page: number,
    limit: number,
  ): Promise<DashboardCasesPage> => {
    const response = await apiGet(
      `/extrajudicial/dashboard/negotiations/funnel/${FUNNEL_STAGE_SLUGS[stage]}/cases`,
      { page: String(page), limit: String(limit) },
    );

    if (!response.ok) {
      await throwApiError(response, 'Erro ao listar casos da etapa do funil');
    }

    const body = await response.json();
    return body?.data ?? body;
  },

  getEngagementCases: async (
    categoria: EngagementCategory,
    page: number,
    limit: number,
  ): Promise<DashboardCasesPage> => {
    const response = await apiGet(
      `/extrajudicial/dashboard/negotiations/engagement/${ENGAGEMENT_CATEGORY_SLUGS[categoria]}/cases`,
      { page: String(page), limit: String(limit) },
    );

    if (!response.ok) {
      await throwApiError(response, 'Erro ao listar casos por perfil de engajamento');
    }

    const body = await response.json();
    return body?.data ?? body;
  },

  startExportCasos: async (
    filters: ExportNegociacaoCasosFilters,
  ): Promise<{ exportId: string }> => {
    const response = await apiPost('/negociacao/casos/export', filters);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao iniciar exportação de negociações');
    }
    return (await response.json()) as { exportId: string };
  },

  /** `signal` cancela a consulta quando o prazo do export acaba (`aguardarExportacaoPronta`). */
  getExportCasosStatus: async (
    exportId: string,
    signal?: AbortSignal,
  ): Promise<ExportNegociacaoCasosStatus> => {
    const response = await apiGet(`/negociacao/casos/export/${exportId}`, undefined, false, signal);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao consultar status da exportação');
    }
    return (await response.json()) as ExportNegociacaoCasosStatus;
  },
};

interface ExportNegociacaoCasosFilters extends NegociacaoPeriodoParams {
  extrajudicialItemId?: string;
  loteId?: string;
  search?: string;
  type?: 'INDIVIDUAL' | 'BULK';
  status?: string;
  statusIn?: string[];
  cadenceBucketIn?: string[];
  humanIntervention?: boolean;
  archived?: boolean;
  tipoEncerramento?: string;
  tipoEncerramentoIn?: string[];
  scenarioTypeIn?: string[];
  pendency?: 'WITH' | 'WITHOUT';
  caseIds?: string[];
  situacaoIdIn?: string[];
  format?: 'csv' | 'xlsx';
}

interface ExportNegociacaoCasosStatus {
  status: 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED';
  signedUrl: string | null;
  truncated: boolean;
  error: string | null;
}
